<template>
    <div class="price-history-chart">
        <div class="row g-2 mb-3">
            <div v-for="item in summary" :key="item.label" class="col-6 col-md">
                <div class="price-history-chart__stat rounded-2 h-100">
                    <div class="small text-muted">{{ item.label }}</div>
                    <div class="fw-semibold">{{ item.value }}</div>
                    <div class="small text-muted">{{ item.note }}</div>
                </div>
            </div>
        </div>
        <div v-if="chart.extreme" class="small text-muted mb-2">
            <span class="me-1" aria-hidden="true">ⓘ</span>Ceny se střídají mezi několika hladinami, nemovitost má nejspíš více
            souběžných inzerátů. Graf proto ukazuje rozpětí, ve kterém se ceny pohybovaly.
        </div>
        <div class="price-history-chart__canvas">
            <canvas ref="canvas"></canvas>
        </div>
        <div class="price-history-chart__legend d-flex flex-wrap justify-content-center mt-2">
            <span v-for="item in legend" :key="item.key" class="text-nowrap">
                <span :class="'price-history-chart__symbol price-history-chart__symbol--' + item.key"></span>{{ item.label }}
            </span>
        </div>
    </div>
</template>
<script lang="ts">
import {
    changeLabels,
    COLORS,
    datasets,
    dateTick,
    eventAxisTicks,
    priceBounds,
    PriceHistoryChartData,
    PriceHistoryRaw,
    priceTick,
    legendItems,
    summaryItems,
    TIME_AXIS,
    timeBounds,
    tooltipFooter,
    tooltipLabel,
    tooltipShows,
    tooltipTitle,
} from '@/components/charts/priceHistory';
import {Chart, Plugin, ScriptableContext} from 'chart.js';
import 'chartjs-adapter-moment';
import {computed, defineComponent, onBeforeUnmount, onMounted, PropType, ref} from 'vue';

/**
 * The chart of the price history of a listing on a time axis: the line of the listing price with the changes marked,
 * the events of the listing and the purchase contract, a summary above it and a legend under it. The font is the
 * one of the page (Noto Sans of the application, IBM Plex Sans of Predict), it is not set here.
 */
export default defineComponent({
    props: {
        chart: {
            type: Object as PropType<PriceHistoryChartData>,
            required: true,
        },
    },
    setup(props) {
        const canvas = ref<HTMLCanvasElement | null>(null);
        const summary = computed(() => summaryItems(props.chart));
        const legend = computed(() => legendItems(props.chart));
        let instance: Chart<'line', PriceHistoryRaw[]> | null = null;

        onMounted(() => {
            const element = canvas.value as HTMLCanvasElement;
            const font = getComputedStyle(element).fontFamily;
            const data = props.chart;
            const prices = priceBounds(data);
            const time = timeBounds(data);

            // A vertical line following the pointer, so that the day the tooltip describes is plain
            const crosshair: Plugin<'line'> = {
                id: 'priceHistoryCrosshair',
                afterDraw(chart) {
                    const active = chart.tooltip?.getActiveElements() ?? [];

                    if (active.length === 0) {
                        return;
                    }

                    const x = active[0].element.x;
                    const {top, bottom} = chart.chartArea;
                    const context = chart.ctx;
                    context.save();
                    context.beginPath();
                    context.moveTo(x, top);
                    context.lineTo(x, bottom);
                    context.lineWidth = 1;
                    context.strokeStyle = 'rgba(33, 37, 41, 0.25)';
                    context.setLineDash([4, 4]);
                    context.stroke();
                    context.restore();
                },
            };

            // The percentage of a change next to its marker; a label too close to one drawn already is left out,
            // the tooltip has it
            const labels = changeLabels(data);
            const percentages: Plugin<'line'> = {
                id: 'priceHistoryChangeLabels',
                afterDatasetsDraw(chart) {
                    if (labels.length === 0) {
                        return;
                    }

                    const meta = chart.getDatasetMeta(0);
                    const drawn: {x: number; y: number}[] = [];
                    const context = chart.ctx;

                    labels.forEach(label => {
                        const element = meta.data[label.index];
                        const x = element.x;
                        const y = element.y + (label.rise ? -12 : 20);

                        if (drawn.some(other => Math.abs(other.x - x) < 44 && Math.abs(other.y - y) < 14)) {
                            return;
                        }

                        drawn.push({x, y});
                        context.save();
                        context.font = `600 11px ${font}`;
                        context.textAlign = 'center';
                        context.fillStyle = label.color;
                        context.fillText(label.text, x, y);
                        context.restore();
                    });
                },
            };

            const areaGradient = (context: ScriptableContext<'line'>) => {
                const area = context.chart.chartArea;

                if (!area) {
                    return 'rgba(80, 155, 254, 0.1)';
                }

                const gradient = context.chart.ctx.createLinearGradient(0, area.top, 0, area.bottom);
                gradient.addColorStop(0, 'rgba(80, 155, 254, 0.22)');
                gradient.addColorStop(1, 'rgba(80, 155, 254, 0)');

                return gradient;
            };

            instance = new Chart<'line', PriceHistoryRaw[]>(element, {
                type: 'line',
                data: {datasets: datasets(data, areaGradient)},
                plugins: [crosshair, percentages],
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    animation: {duration: 600},
                    interaction: {mode: 'nearest', axis: 'x', intersect: false},
                    layout: {padding: {top: 8, right: 8}},
                    font: {family: font},
                    plugins: {
                        legend: {display: false},
                        tooltip: {
                            backgroundColor: 'rgba(23, 28, 40, 0.92)',
                            padding: 12,
                            cornerRadius: 8,
                            displayColors: false,
                            titleFont: {family: font, weight: '600', size: 12},
                            bodyFont: {family: font, size: 13},
                            footerFont: {family: font, size: 12, weight: '400'},
                            filter: tooltipShows,
                            callbacks: {
                                title: tooltipTitle,
                                label: item => tooltipLabel(item, data.currency),
                                footer: items => tooltipFooter(items, data.currency),
                            },
                        },
                    },
                    scales: {
                        x: {
                            type: 'time',
                            ...time,
                            time: TIME_AXIS,
                            afterBuildTicks: eventAxisTicks(data),
                            grid: {color: COLORS.grid, drawBorder: false, drawTicks: false},
                            ticks: {
                                color: COLORS.muted,
                                autoSkip: false,
                                minRotation: 60,
                                maxRotation: 60,
                                padding: 6,
                                callback: dateTick,
                                font: {family: font, size: 12},
                            },
                        },
                        y: {
                            suggestedMin: prices.min,
                            suggestedMax: prices.max,
                            grid: {color: COLORS.grid, drawBorder: false},
                            ticks: {color: COLORS.muted, maxTicksLimit: 6, callback: priceTick, font: {family: font, size: 12}},
                        },
                    },
                },
            });
        });

        onBeforeUnmount(() => {
            instance?.destroy();
            instance = null;
        });

        return {canvas, summary, legend};
    },
});
</script>
